import { z } from 'zod';
import { ID_PATTERN } from './ids.js';

export const moduleIdSchema = z.string().regex(/^[a-z][a-z0-9-]{1,39}$/);
export const entityTypeSchema = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/);
export const idSchema = z.string().regex(ID_PATTERN, 'Invalid identifier');

/** A reference inside the caller's workspace. The workspace is always implied by the session. */
export const localRefSchema = z.object({
  moduleId: moduleIdSchema,
  entityType: entityTypeSchema,
  entityId: idSchema,
});
export type LocalRef = z.infer<typeof localRefSchema>;

export const entityRefSchema = localRefSchema.extend({ workspaceId: idSchema });
export type EntityRef = z.infer<typeof entityRefSchema>;

const enc = encodeURIComponent;

/** Unambiguous canonical key for a complete reference tuple. `:` is escaped inside parts. */
export function entityKey(ref: EntityRef): string {
  return [ref.workspaceId, ref.moduleId, ref.entityType, ref.entityId].map(enc).join(':');
}

/** Workspace-less key used by browsers and renderers. Round-trips to exactly one LocalRef. */
export function nodeKey(ref: LocalRef): string {
  return [ref.moduleId, ref.entityType, ref.entityId].map(enc).join(':');
}

export function parseNodeKey(key: string): LocalRef {
  const parts = key.split(':');
  if (parts.length !== 3) throw new Error(`Invalid node key: ${key}`);
  const [moduleId, entityType, entityId] = parts.map((p) => decodeURIComponent(p));
  return localRefSchema.parse({ moduleId, entityType, entityId });
}

export function parseEntityKey(key: string): EntityRef {
  const parts = key.split(':');
  if (parts.length !== 4) throw new Error(`Invalid entity key: ${key}`);
  const [workspaceId, moduleId, entityType, entityId] = parts.map((p) => decodeURIComponent(p));
  return entityRefSchema.parse({ workspaceId, moduleId, entityType, entityId });
}

export const nodeKeySchema = z.string().max(200).transform((value, ctx) => {
  try {
    return parseNodeKey(value);
  } catch {
    ctx.addIssue({ code: 'custom', message: 'Invalid entity key' });
    return z.NEVER;
  }
});

export function typeKey(ref: Pick<LocalRef, 'moduleId' | 'entityType'>): string {
  return `${ref.moduleId}:${ref.entityType}`;
}

export function toLocalRef(ref: EntityRef | LocalRef): LocalRef {
  return { moduleId: ref.moduleId, entityType: ref.entityType, entityId: ref.entityId };
}

export function sameRef(a: LocalRef, b: LocalRef): boolean {
  return a.moduleId === b.moduleId && a.entityType === b.entityType && a.entityId === b.entityId;
}
